import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ClipboardList, Sparkle, Sun } from 'lucide-react';

const vehicleSizes = [
  { size: 'Small', examples: 'Coupes, sedans' },
  { size: 'Medium', examples: 'Small SUVs, crossovers' },
  { size: 'Large', examples: 'Large SUVs, trucks, vans' },
  { size: 'XL / Oversized', examples: '3-row SUVs, work vans, large trucks' },
];

const packages = [
  {
    id: 'basic',
    title: 'Basic Wash & Vacuum',
    description: 'Ideal for regular maintenance and touch-ups between major details.',
    price: {
      small: { light: '$50–$70', dirty: '$70–$90' },
      medium: { light: '$60–$80', dirty: '$80–$100' },
      large: { light: '$70–$90', dirty: '$90–$120' },
    },
    includes: [
      'Exterior hand wash & dry',
      'Tire & rim clean',
      'Interior vacuum',
      'Dashboard & surface wipe-down',
      'Windows inside/out',
    ],
    accent: 'from-brand-500 to-brand-300',
  },
  {
    id: 'full',
    title: 'Full Interior & Exterior Detail',
    description: 'Deep clean for vehicles that need a serious refresh.',
    price: {
      small: { light: '$140–$160', dirty: '$160–$180' },
      medium: { light: '$160–$180', dirty: '$180–$200' },
      large: { light: '$180–$200', dirty: '$200–$230' },
    },
    includes: [
      'Everything in Basic',
      'Carpet shampoo or leather conditioning',
      'Deep clean vents, cupholders & door jambs',
      'Wax or paint sealant',
      'Tire shine & exterior trim dressed',
    ],
    accent: 'from-brand-600 to-rose-400',
  },
  {
    id: 'premium',
    title: 'Premium / Full Restoration',
    description: 'Showroom or resale prep for heavily soiled vehicles.',
    price: {
      small: { light: '$220–$250', dirty: '$250–$280' },
      medium: { light: '$250–$280', dirty: '$280–$320' },
      large: { light: '$280–$320', dirty: '$320–$400' },
    },
    includes: [
      'Everything in Full Detail',
      'Steam clean interior + odor neutralizing',
      'Clay bar treatment & paint decontamination',
      'Engine bay clean & dress',
      'Ozone treatment available on request',
    ],
    accent: 'from-rose-500 to-amber-400',
  },
];

const conditionAdjustments = [
  { condition: 'Light', fee: 'Base price', description: 'Regular use, minor dirt.' },
  {
    condition: 'Moderate',
    fee: '+$20–$40',
    description: 'Pet hair, moderate stains, light odors.',
  },
  {
    condition: 'Heavy / Severe',
    fee: '+$50–$100',
    description: 'Mud, sand, smoke, spills, or bio cleanup.',
  },
];

const addOns = [
  { name: 'Headlight restoration', price: '$40–$60' },
  { name: 'Engine bay cleaning', price: '$40–$70' },
  { name: 'Pet hair removal', price: '$30–$60' },
  { name: 'Odor treatment / Ozone', price: '$50–$80' },
  { name: 'Ceramic spray sealant', price: '$50–$100' },
  { name: 'Interior disinfectant', price: '$30–$50' },
];

const pricingTips = [
  'Check nearby competitors—aim to be 10–15% better value, not just cheaper.',
  'Use “starting at” pricing in ads (e.g. “Full Detail starting at $160”).',
  'Add a mobile service premium of $20–$40 for driveway appointments.',
  'Reward repeat customers with loyalty perks or bundled pricing.',
];

export default function Services() {
  const navigate = useNavigate();

  const handleReserve = (packageId) => {
    navigate(`/contact?service=${packageId}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <section className="relative overflow-hidden py-20">
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#20070b] via-[#160508] to-[#20070b]" />
      <div className="mx-auto flex max-w-6xl flex-col gap-16 px-4">
        <header className="space-y-4 text-center">
          <motion.span
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1 text-xs font-semibold uppercase tracking-[0.3em] text-white/70"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
          >
            Five-Step Pricing System
          </motion.span>
          <motion.h2
            className="text-3xl font-bold text-white sm:text-4xl"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1, duration: 0.5 }}
          >
            Simple, transparent mobile detailing packages
          </motion.h2>
          <motion.p
            className="mx-auto max-w-2xl text-sm text-white/70 sm:text-base"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.15, duration: 0.5 }}
          >
            Start with the size of the vehicle, pick the service level, adjust for condition, and
            finish with any add-ons. Every quote and approval follows the same flow so nobody is
            surprised on-site.
          </motion.p>
        </header>

        <motion.div
          className="space-y-6 rounded-3xl border border-white/10 bg-[#1c0d11]/80 p-6 shadow-xl shadow-black/40 sm:p-8"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-200">
                Step 1
              </p>
              <h3 className="text-2xl font-semibold text-white">Group vehicles by size</h3>
            </div>
            <p className="max-w-md text-sm text-white/60">
              Use this quick reference chart so every team member quotes the same base price.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {vehicleSizes.map((item) => (
              <div
                key={item.size}
                className="flex items-center justify-between rounded-2xl border border-white/5 bg-white/5 px-4 py-4"
              >
                <div>
                  <p className="text-sm font-semibold text-white">{item.size}</p>
                  <p className="text-xs text-white/60">{item.examples}</p>
                </div>
                <ClipboardList className="hidden h-8 w-8 text-brand-200 sm:block" />
              </div>
            ))}
          </div>
          <p className="text-xs text-white/50">
            * XL / Oversized vehicles usually add +$30 for extra time and materials.
          </p>
        </motion.div>

        <motion.div
          className="space-y-8 rounded-3xl border border-white/10 bg-[#2a1016]/70 p-6 shadow-xl shadow-black/40 sm:p-8"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1, duration: 0.5 }}
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-200">
                Step 2
              </p>
              <h3 className="text-2xl font-semibold text-white">Choose a service package</h3>
            </div>
            <p className="max-w-md text-sm text-white/60">
              Packages stack on top of each other so upgrades are easy to explain and approve.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {packages.map((pkg) => (
              <div
                key={pkg.id}
                className="flex flex-col gap-6 rounded-3xl border border-white/5 bg-white/[0.04] p-6 shadow-lg shadow-black/30"
              >
                <div className="space-y-3">
                  <span
                    className={`inline-block rounded-full bg-gradient-to-r ${pkg.accent} px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-white/90`}
                  >
                    {pkg.title}
                  </span>
                  <p className="text-sm text-white/70">{pkg.description}</p>
                </div>

                <div className="overflow-hidden rounded-2xl border border-white/10">
                  <table className="w-full text-sm text-white/80">
                    <thead className="bg-white/10 text-xs uppercase tracking-widest text-white/60">
                      <tr>
                        <th className="px-3 py-2 text-left">Size</th>
                        <th className="px-3 py-2 text-left">Light</th>
                        <th className="px-3 py-2 text-left">Dirty</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {Object.entries(pkg.price).map(([size, range]) => (
                        <tr key={size} className="bg-black/30">
                          <td className="px-3 py-2 capitalize text-white">{size}</td>
                          <td className="px-3 py-2">{range.light}</td>
                          <td className="px-3 py-2">{range.dirty}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div>
                  <p className="text-xs uppercase tracking-[0.25em] text-white/50">Includes</p>
                  <ul className="mt-3 flex flex-col gap-2 text-sm text-white/70">
                    {pkg.includes.map((item) => (
                      <li key={item} className="flex items-start gap-2">
                        <Sparkle className="mt-0.5 h-4 w-4 text-brand-200" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <button
                  type="button"
                  onClick={() => handleReserve(pkg.id)}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white/10 px-4 py-3 text-sm font-semibold text-white transition hover:bg-white/20"
                >
                  Reserve this package
                </button>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          className="grid gap-6 rounded-3xl border border-white/10 bg-[#2a1016]/70 p-6 shadow-xl shadow-black/40 sm:grid-cols-2 sm:p-8"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.15, duration: 0.5 }}
        >
          <div className="space-y-4">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-200">
              Step 3
            </p>
            <h3 className="text-2xl font-semibold text-white">Add condition-based adjustments</h3>
            <p className="text-sm text-white/60">
              Quickly explain dirt level surcharges and get sign-off before you set up equipment.
            </p>
            <div className="overflow-hidden rounded-2xl border border-white/10">
              <table className="w-full text-sm text-white/80">
                <thead className="bg-white/10 text-xs uppercase tracking-widest text-white/60">
                  <tr>
                    <th className="px-3 py-2 text-left">Condition</th>
                    <th className="px-3 py-2 text-left">Add-on fee</th>
                    <th className="px-3 py-2 text-left">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5 bg-black/30">
                  {conditionAdjustments.map((row) => (
                    <tr key={row.condition}>
                      <td className="px-3 py-2 text-white">{row.condition}</td>
                      <td className="px-3 py-2">{row.fee}</td>
                      <td className="px-3 py-2 text-white/70">{row.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="space-y-4 rounded-3xl border border-white/10 bg-[#1c0d11]/80 p-6">
            <h4 className="text-lg font-semibold text-white">Approval script</h4>
            <p className="text-sm text-white/70">
              “Based on the amount of pet hair we recommend the{' '}
              <strong>Moderate</strong> tier (+$20–$40). We will confirm the exact number when we
              arrive so there are no surprises.”
            </p>
            <p className="text-xs text-white/50">
              Pro move: snap a quick photo during intake and attach it to the approval request.
            </p>
          </div>
        </motion.div>

        <motion.div
          className="grid gap-6 lg:grid-cols-[2fr_1fr]"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          <div className="space-y-6 rounded-3xl border border-white/10 bg-[#2a1016]/70 p-6 shadow-xl shadow-black/40">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-200">
                  Step 4
                </p>
                <h3 className="text-2xl font-semibold text-white">Optional add-ons</h3>
              </div>
              <p className="text-sm text-white/60">Boost margins by pairing the right extras.</p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {addOns.map((item) => (
                <div
                  key={item.name}
                  className="flex items-center justify-between rounded-2xl border border-white/5 bg-white/5 px-4 py-3 text-sm text-white/80"
                >
                  <span>{item.name}</span>
                  <span className="font-medium text-white">{item.price}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-5 rounded-3xl border border-white/10 bg-[#1c0d11]/80 p-6 shadow-xl shadow-black/40">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-200">
                Step 5
              </p>
              <h3 className="text-2xl font-semibold text-white">Pricing strategy tips</h3>
            </div>
            <ul className="space-y-3 text-sm text-white/70">
              {pricingTips.map((tip) => (
                <li key={tip} className="flex items-start gap-2">
                  <Sun className="mt-0.5 h-4 w-4 text-brand-200" />
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          </div>
        </motion.div>

        <motion.div
          className="rounded-3xl border border-brand-400/30 bg-brand-500/10 p-6 text-center text-sm text-white/80 shadow-lg shadow-brand-500/20 sm:p-8"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.25, duration: 0.5 }}
        >
          Ready to lock in your pricing? Download the booking checklist we use on-site or customize
          it with your own branding.{' '}
          <a href="/contact" className="font-semibold text-brand-200 hover:text-brand-100">
            Reach out to request a copy.
          </a>
        </motion.div>
      </div>
    </section>
  );
}
